import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge, { formatCurrency, overdueTone } from "@/components/StatusBadge";
import { useNavigate } from "react-router-dom";
import { RotateCcw, Search } from "lucide-react";
import { useTableSort } from "@/lib/useTableSort";

export default function PendingPayments() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();

  const [projectQ, setProjectQ] = useState("");
  const [clientQ, setClientQ] = useState("");
  const [bdQ, setBdQ] = useState("");

  useEffect(() => {
    api.get("/api/pending-payments").then((r) => setItems(r.data));
    api.get("/api/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const userMap = useMemo(() => {
    const m = new Map(); users.forEach((u) => m.set(u.id, u)); return m;
  }, [users]);

  const filtered = useMemo(() => {
    const pq = projectQ.trim().toLowerCase();
    const cq = clientQ.trim().toLowerCase();
    const bq = bdQ.trim().toLowerCase();
    return items.filter((it) => {
      if (pq) {
        const nm = (it.project?.project_name || "").toLowerCase();
        const code = (it.project?.project_code || "").toLowerCase();
        if (!nm.includes(pq) && !code.includes(pq)) return false;
      }
      if (cq) {
        const fn = (it.client?.firm_name || "").toLowerCase();
        if (!fn.includes(cq)) return false;
      }
      if (bq) {
        const bdId = it.project?.business_development_owner_id;
        const bdName = (userMap.get(bdId)?.name || it.project?.business_development_owner_name_snapshot || "").toLowerCase();
        if (!bdName.includes(bq)) return false;
      }
      return true;
    });
  }, [items, projectQ, clientQ, bdQ, userMap]);

  const resetFilters = () => { setProjectQ(""); setClientQ(""); setBdQ(""); };

  return (
    <div className="space-y-6" data-testid="pending-payments-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold tracking-tight">Pending Payments</h1>
        <p className="text-sm text-slate-500 mt-1">Invoices with an outstanding balance.</p>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-none p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Project name / code</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={projectQ} onChange={(e) => setProjectQ(e.target.value)} placeholder="e.g. Aurora" className="pl-8" data-testid="pp-filter-project" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Client name</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={clientQ} onChange={(e) => setClientQ(e.target.value)} placeholder="e.g. Beacon" className="pl-8" data-testid="pp-filter-client" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">BD name</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={bdQ} onChange={(e) => setBdQ(e.target.value)} placeholder="e.g. Sarah" className="pl-8" data-testid="pp-filter-bd" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={resetFilters} data-testid="pp-reset-filters">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
              </Button>
              <PendingSortDropdown />
            </div>
            <div className="text-xs text-slate-500">
              {filtered.length} of {items.length}
            </div>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-none p-4">
        <div className="overflow-x-auto">
        <PendingPaymentsTable rows={filtered} userMap={userMap} navigate={navigate} />
        </div>
      </Card>
    </div>
  );
}

/** Shared newest/oldest dropdown for Pending Payments — reads/writes the same
 *  localStorage key as the table so both stay in sync. */
function PendingSortDropdown() {
  const [sortBy, setSortBy] = useState(() => {
    try {
      const raw = window.localStorage.getItem("sort:pending-payments");
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.key && p?.dir) return p;
      }
    } catch { /* ignore */ }
    return { key: "days_overdue", dir: "desc" };
  });
  // Poll storage in case the table headers toggle the sort — keeps dropdown in sync
  React.useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "sort:pending-payments" && e.newValue) {
        try { const p = JSON.parse(e.newValue); if (p?.key && p?.dir) setSortBy(p); } catch { /* ignore */ }
      }
    };
    window.addEventListener("storage", onStorage);
    const t = setInterval(() => {
      try {
        const raw = window.localStorage.getItem("sort:pending-payments");
        if (!raw) return;
        const p = JSON.parse(raw);
        if (p?.key && p?.dir && (p.key !== sortBy.key || p.dir !== sortBy.dir)) setSortBy(p);
      } catch { /* ignore */ }
    }, 750);
    return () => { window.removeEventListener("storage", onStorage); clearInterval(t); };
  }, [sortBy.key, sortBy.dir]);
  const value = sortBy.key === "invoice_date"
    ? (sortBy.dir === "desc" ? "newest" : "oldest")
    : "custom";
  const apply = (v) => {
    const next = v === "newest"
      ? { key: "invoice_date", dir: "desc" }
      : { key: "invoice_date", dir: "asc" };
    setSortBy(next);
    try { window.localStorage.setItem("sort:pending-payments", JSON.stringify(next)); } catch { /* ignore */ }
    // Nudge the useTableSort hook (which reads only at mount) — reload the page fragment via a soft event.
    window.dispatchEvent(new StorageEvent("storage", { key: "sort:pending-payments", newValue: JSON.stringify(next) }));
  };
  return (
    <Select value={value} onValueChange={apply}>
      <SelectTrigger className="w-40" data-testid="pp-sort-order"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="newest">Newest first</SelectItem>
        <SelectItem value="oldest">Oldest first</SelectItem>
        {value === "custom" && <SelectItem value="custom" disabled>Custom · {sortBy.key}</SelectItem>}
      </SelectContent>
    </Select>
  );
}

function PendingPaymentsTable({ rows, userMap, navigate }) {
  const columns = [
    { key: "client", label: "Client", getValue: (r) => r.client?.firm_name },
    { key: "project", label: "Project", getValue: (r) => r.project?.project_name },
    { key: "bd", label: "BD", getValue: (r) => userMap.get(r.project?.business_development_owner_id)?.name || r.project?.business_development_owner_name_snapshot },
    { key: "invoice_number", label: "Invoice #", getValue: (r) => r.invoice?.invoice_number },
    { key: "invoice_date", label: "Invoice Date", getValue: (r) => r.invoice?.invoice_date },
    { key: "due_date", label: "Due", getValue: (r) => r.invoice?.due_date },
    { key: "amount", label: "Amount", numeric: true, align: "right", getValue: (r) => r.invoice?.total_invoice_amount },
    { key: "received", label: "Received", numeric: true, align: "right", getValue: (r) => r.invoice?.amount_received },
    { key: "outstanding", label: "Outstanding", numeric: true, align: "right", getValue: (r) => r.invoice?.outstanding_amount },
    { key: "days_overdue", label: "Days Overdue", numeric: true, align: "right", getValue: (r) => r.invoice?.days_overdue },
    { key: "status", label: "Status", getValue: (r) => r.invoice?.payment_status },
  ];
  const { sortedRows, header } = useTableSort(rows, columns, "days_overdue", "desc", { storageKey: "pending-payments" });
  return (
    <table className="data-table w-full">
      <thead>{header}</thead>
      <tbody>
        {sortedRows.map((it) => {
          const bdName = userMap.get(it.project?.business_development_owner_id)?.name || it.project?.business_development_owner_name_snapshot || "—";
          return (
            <tr key={it.invoice.id}>
              <td className="font-medium">{it.client?.firm_name}</td>
              <td>{it.project?.project_name}</td>
              <td className="text-slate-600 text-sm">{bdName}</td>
              <td className="font-mono text-xs">{it.invoice.invoice_number}</td>
              <td>{it.invoice.invoice_date?.slice(0, 10)}</td>
              <td>{it.invoice.due_date?.slice(0, 10)}</td>
              <td className="text-right">{formatCurrency(it.invoice.total_invoice_amount, it.invoice.currency)}</td>
              <td className="text-right text-emerald-700">{formatCurrency(it.invoice.amount_received, it.invoice.currency)}</td>
              <td className="text-right text-amber-700">{formatCurrency(it.invoice.outstanding_amount, it.invoice.currency)}</td>
              <td className={`text-right ${overdueTone(it.invoice.days_overdue)}`}>{it.invoice.days_overdue}</td>
              <td><StatusBadge status={it.invoice.payment_status} /></td>
              <td><Button size="sm" variant="outline" onClick={() => navigate(`/invoices`)}>Record</Button></td>
            </tr>
          );
        })}
        {sortedRows.length === 0 && <tr><td colSpan={12} className="text-center text-slate-500 py-8">No outstanding invoices. 🎉</td></tr>}
      </tbody>
    </table>
  );
}
